import { serialize, deserialize } from '@authy/lib-common/utils/misc'
import { TRPCClientError } from '@authy/lib-client/trpc'

import { ApplicationKey } from './types'

export const createCache = () => ({
    appKey: <ApplicationKey | null>null,
    tenantShortId: '',

    init(appKey: ApplicationKey) {
        this.appKey = appKey
        this.tenantShortId = window.localStorage.getItem(`${this.appKey}.tenantShortId`) || ''
    },

    get cacheRootKey() {
        if (!this.tenantShortId) {
            throw new Error('Missing tenant id')
        }

        return `cache.${this.appKey}.${this.tenantShortId}`
    },

    setTenantShortId(tenantShortId: string) {
        if (this.tenantShortId && this.tenantShortId !== tenantShortId) {
            // -- Clear any previous tenant's stuff first
            this._clear()
        }

        this.tenantShortId = tenantShortId
        window.localStorage.setItem(`${this.appKey}.tenantShortId`, this.tenantShortId)
    },

    async get<TData>(
        key: string,
        query: (updateAfter?: Date) => Promise<TData>,
        update: (data: TData) => void,
        { ignoreNotFoundError = false }: { ignoreNotFoundError?: boolean } = {}
    ): Promise<void> {
        const cached = this._tryLoadFromStorage(key)
        if (cached) {
            // Immediatly update witch cached value
            update(cached.data as TData)

            // Query in background for an updatd value
            query(cached.timestamp)
                .then((data) => {
                    // Store new value
                    this._saveToStorage(key, data)

                    // Update now with updated value
                    update(data as TData)
                })
                .catch((error) => {
                    if (error instanceof TRPCClientError) {
                        // Ignore not found as that indicates no updates
                        if (!(error.data?.code === 'NOT_FOUND' && ignoreNotFoundError)) {
                            throw error
                        }
                    }
                })
        } else {
            // Wait to get first time value
            const data = await query()

            // Store it into cache
            this._saveToStorage(key, data)

            // Return it by calling our update initially
            update(data)
        }
    },

    _saveToStorage(key: string, data: any) {
        const value = serialize({ timestamp: new Date(), data })

        if (typeof value === 'string') {
            window.localStorage.setItem(`${this.cacheRootKey}.${key}`, value)
        }
    },

    _tryLoadFromStorage(key: string): { timestamp: Date; data: any } | null {
        const value = window.localStorage.getItem(`${this.cacheRootKey}.${key}`)

        if (value) {
            try {
                const { timestamp, data } = deserialize(value)
                if (timestamp && data) {
                    return { timestamp, data }
                }
            } catch {
                // ignore we return null later
            }
        }

        return null
    },

    _clear() {
        const keysToDelete: string[] = []

        for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i)
            if (key && key.startsWith(this.cacheRootKey)) {
                keysToDelete.push(key)
            }
        }

        keysToDelete.forEach((key) => window.localStorage.removeItem(key))

        window.localStorage.removeItem(`${this.appKey}.tenant`)
    },
})

export type Cache = ReturnType<typeof createCache>
